import * as React from 'react';
import { Grid } from '@material-ui/core';
import { EMPTY_TEXT, Magnetic as Mi } from '@mitchell/react.component.ux';
import { TranslationService, EnterpriseDocStoreService } from "../../../common";
import { IntelligentPhotoCaptureScoreName, IntelligentPhotoCaptureViewType, PhotoCaptureAttributeType, IntelligentPhotoCaptureImgAnalyzerScoreStatus } from '../../enums';
import { GPCLoaderConstants, IntelligentImageAnalyzerConstant, IntelligentImageAnalyzerCutOff, IntelligentPhotoCapturePhotoPreviewConstants, IntelligentPhotoCatureImageConstant, IQA_SCREEN_DELAY_MS } from '../../constants';
import { createDefaultImageDetailsLoaders, getIQAHistoryDetails, getScreenTitleText } from '../../utilities';
import { StyledDivSVGImg, StyledIQAGridContainerImage, StyledIQAGridContainerItem, StyledIQAImageLoader, StyledImgAnalyzerIcon } from './IntelligentPhotoCaptureImageAnalyzerView.style';
import { IntelligentPBEContainerComponent } from './IntelligentPBEContainerComponent';
import { IntelligentPhotoCaptureScreenTitle } from './IntelligentPhotoCaptureScreenTitle';
import { IntelligentPhotoCaptureImageSection } from './IntelligentPhotoCaptureImageSection';
import { StyledSecondTextTitle } from './IntelligentPageGuidanceInfo.style';
const LoaderProgressImage = ({ scoreStatus }) => {
    const imageMap = {
        [IntelligentPhotoCaptureImgAnalyzerScoreStatus.LOADING]: (React.createElement(StyledIQAImageLoader, { src: IntelligentPhotoCatureImageConstant.imgAnalyzerProgress.image, alt: GPCLoaderConstants.LOADING })),
        [IntelligentPhotoCaptureImgAnalyzerScoreStatus.SUCCESS]: (React.createElement(StyledImgAnalyzerIcon, { className: "mi-icon-complete-checkmark", colorCode: Mi.colorGreen })),
        [IntelligentPhotoCaptureImgAnalyzerScoreStatus.FAIL]: (React.createElement(StyledImgAnalyzerIcon, { className: "mi-icon-close", colorCode: Mi.colorRed }))
    };
    return imageMap[scoreStatus];
};
export const ScoreLoader = ({ scoreName, loaders, isLoading }) => {
    const scoreMap = {
        [IntelligentPhotoCaptureScoreName.BRIGHTNESS]: loaders.isBrightnessScoreLoader,
        [IntelligentPhotoCaptureScoreName.CONTRAST]: loaders.isContrastScoreLoader,
        [IntelligentPhotoCaptureScoreName.COLOR]: loaders.isGammaScoreLoader,
        [IntelligentPhotoCaptureScoreName.FOCUS]: loaders.isBlurScoreLoader,
        [IntelligentPhotoCaptureScoreName.LIGHTING]: loaders.isNoiseScoreLoader
    };
    const getScoreStatus = (score) => {
        if (isLoading || score === null)
            return IntelligentPhotoCaptureImgAnalyzerScoreStatus.LOADING;
        return score
            ? IntelligentPhotoCaptureImgAnalyzerScoreStatus.SUCCESS
            : IntelligentPhotoCaptureImgAnalyzerScoreStatus.FAIL;
    };
    return scoreName in scoreMap ? (React.createElement(LoaderProgressImage, { "data-testid": `score-loader-${scoreName}`, scoreStatus: getScoreStatus(scoreMap[scoreName]) })) : (React.createElement("div", { "data-testid": 'score-loader-unknown' }));
};
export const IntelligentPhotoCaptureImageAnalyzerView = props => {
    const { appTheme, onViewChange, imageAnalyzerData, questionSubType, sequenceScreenName, serviceFailStatus, files, updatePageAttachmentHistory } = props;
    const [imgData, setImgData] = React.useState(undefined);
    const [shouldPageRedirect, setShouldPageRedirect] = React.useState(false);
    const [loaders, setLoaders] = React.useState(createDefaultImageDetailsLoaders());
    const [isLoading, setIsLoading] = React.useState(false);
    const translateFn = TranslationService.translate;
    React.useEffect(() => {
        setImgData(imageAnalyzerData);
    }, [imageAnalyzerData]);
    React.useEffect(() => {
        if (files && files.length) {
            setImgData(undefined);
            setShouldPageRedirect(false);
            setLoaders(createDefaultImageDetailsLoaders());
            setIsLoading(true);
        }
    }, [files]);
    React.useEffect(() => {
        if (imgData && imgData.brightnessScore) {
            const updateLoader = (score, lowCutOff, highCutOff) => score >= lowCutOff && score <= highCutOff;
            setLoaders({
                isBrightnessScoreLoader: updateLoader(imgData.brightnessScore, IntelligentImageAnalyzerCutOff.BRIGHTNESS_LOW_CUTOFF, IntelligentImageAnalyzerCutOff.BRIGHTNESS_HIGH_CUTOFF),
                isContrastScoreLoader: updateLoader(imgData.contrastScore, IntelligentImageAnalyzerCutOff.CONTRAST_LOW_CUTOFF, IntelligentImageAnalyzerCutOff.CONTRAST_HIGH_CUTOFF),
                isGammaScoreLoader: updateLoader(imgData.gammaScore, IntelligentImageAnalyzerCutOff.COLOR_LOW_CUTOFF, IntelligentImageAnalyzerCutOff.COLOR_HIGH_CUTOFF),
                isBlurScoreLoader: updateLoader(imgData.blurScore, IntelligentImageAnalyzerCutOff.FOCUS_LOW_CUTOFF, IntelligentImageAnalyzerCutOff.FOCUS_HIGH_CUTOFF),
                isNoiseScoreLoader: updateLoader(imgData.noiseScore, IntelligentImageAnalyzerCutOff.LIGHTING_LOW_CUTOFF, IntelligentImageAnalyzerCutOff.LIGHTING_HIGH_CUTOFF)
            });
            setIsLoading(false);
            setShouldPageRedirect(true);
        }
    }, [imgData]);
    const handleHistoryInfo = () => {
        if (files && files[0] && files[0].docUID) {
            updatePageAttachmentHistory(files[0].docUID, getIQAHistoryDetails(imageAnalyzerData));
        }
    };
    React.useEffect(() => {
        handleHistoryInfo();
    }, [imageAnalyzerData]);
    React.useEffect(() => {
        let timeOut;
        if (shouldPageRedirect && imgData && imgData.brightnessScore) {
            const { isBrightnessScoreLoader, isContrastScoreLoader, isGammaScoreLoader, isBlurScoreLoader, isNoiseScoreLoader } = loaders;
            timeOut = setTimeout(() => {
                if (shouldPageRedirect && imgData && imgData.brightnessScore) {
                    switch (questionSubType) {
                        case PhotoCaptureAttributeType.SINGLE_WIDE_SHOT:
                        case PhotoCaptureAttributeType.THREE_CLOSE_UP:
                        case PhotoCaptureAttributeType.FOUR_CORNERS_CAPTURE:
                        case PhotoCaptureAttributeType.WIDE_ENHANCED:
                        case PhotoCaptureAttributeType.CLOSEUP_ENHANCED:
                            if (isBrightnessScoreLoader &&
                                isContrastScoreLoader &&
                                isGammaScoreLoader &&
                                isBlurScoreLoader &&
                                isNoiseScoreLoader) {
                                onViewChange(IntelligentPhotoCaptureViewType.COMMENT, sequenceScreenName);
                                break;
                            }
                            else {
                                onViewChange(IntelligentPhotoCaptureViewType.RETAKE_PAGE, sequenceScreenName);
                                break;
                            }
                        default: {
                            onViewChange(IntelligentPhotoCaptureViewType.COMMENT, sequenceScreenName);
                            break;
                        }
                    }
                }
            }, 4000);
        }
        return () => {
            if (timeOut) {
                clearTimeout(timeOut);
            }
        };
    }, [shouldPageRedirect, loaders, sequenceScreenName, imgData]);
    React.useEffect(() => {
        let timeOut;
        if (serviceFailStatus) {
            timeOut = setTimeout(() => {
                onViewChange(IntelligentPhotoCaptureViewType.RETAKE_PAGE, sequenceScreenName);
            }, IQA_SCREEN_DELAY_MS);
        }
        return () => {
            timeOut && clearTimeout(timeOut);
        };
    }, [serviceFailStatus]);
    return (React.createElement(IntelligentPBEContainerComponent, { islanguageHide: true, appTheme: appTheme, isFooter: false, hasMultipleButton: false, movePrevious: () => onViewChange(IntelligentPhotoCaptureViewType.PREVIEW, sequenceScreenName), shouldShowAvatar: true },
        React.createElement(IntelligentPhotoCaptureScreenTitle, { firstText: translateFn(`${IntelligentPhotoCapturePhotoPreviewConstants.ANALYSING_PHOTO}`), secondText: translateFn(`${getScreenTitleText(questionSubType, sequenceScreenName)}`) }),
        React.createElement(IntelligentPhotoCaptureImageSection, { imageSrc: EnterpriseDocStoreService.createFileUrl((files && files.length && files[0].thumbnail && files[0].thumbnail.docUID) || EMPTY_TEXT) }),
        React.createElement(StyledIQAGridContainerImage, null,
            React.createElement(Grid, { container: true, spacing: 4 }, IntelligentImageAnalyzerConstant &&
                IntelligentImageAnalyzerConstant.length &&
                IntelligentImageAnalyzerConstant.map((key, index) => (React.createElement(Grid, { key: index, container: true, direction: "row", justify: "space-between", alignItems: "center" },
                    React.createElement(StyledIQAGridContainerItem, { item: true, xs: 2 },
                        React.createElement(StyledDivSVGImg, { svgKey: key.image })),
                    React.createElement(StyledIQAGridContainerItem, { item: true, xs: 8 },
                        React.createElement(StyledSecondTextTitle, null, translateFn(`${key.text}`))),
                    React.createElement(StyledIQAGridContainerItem, { item: true, xs: 2 },
                        React.createElement(ScoreLoader, { scoreName: key.text, loaders: loaders, isLoading: isLoading })))))))));
};
//# sourceMappingURL=IntelligentPhotoCaptureImageAnalyzerView.js.map